import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { api } from "../api";
import { city } from "../components/ui";

// The question: which AWS services do several projects share, and who uses each?

interface SharedService {
  id: string;
  slug: string;
  name: string;
  kind: string;
  region: string;
  identifier: string;
  description: string;
  monthlyUsd: number | null;
  projects: { slug: string; name: string; usage: string }[];
}

export function ServicesPage() {
  const q = useQuery({ queryKey: ["services"], queryFn: () => api<SharedService[]>("/services") });
  const total = (q.data ?? []).reduce((n, s) => n + (s.monthlyUsd ?? 0), 0);
  return (
    <div className="canvas">
      <div className="title-row">
        <div>
          <h1>Shared services</h1>
          <p className="sub">
            {q.data ? `${q.data.length} services shared across projects, about $${Math.round(total)} a month together (estimates).` : "AWS services several projects use, and who uses each."}
          </p>
        </div>
      </div>
      <section className="panel flush" aria-label="Shared services">
        {q.error && <div className="item"><span className="error">{q.error.message}</span></div>}
        <div className="table-wrap">
          <table>
            <thead><tr><th>Service</th><th>Where</th><th>Used by</th><th className="num">Per month</th></tr></thead>
            <tbody>
              {q.data?.map((s) => (
                <tr key={s.id}>
                  <td>
                    <div className="cell-main">{s.name}</div>
                    <div className="cell-sub">{s.kind} · {s.description}</div>
                    <div className="cell-sub mono truncate" style={{ maxWidth: 360 }} title={s.identifier}>{s.identifier}</div>
                  </td>
                  <td>{s.region && s.region !== "global" ? city(s.region) : "Global"}</td>
                  <td>
                    {s.projects.length === 0 && <span className="muted">no project yet</span>}
                    <div style={{ display: "grid", gap: 4 }}>
                      {s.projects.map((p) => (
                        <span key={p.slug}>
                          <Link to={`/p/${p.slug}`} style={{ textDecoration: "underline", textUnderlineOffset: 3 }}>{p.name.split(" — ")[0]}</Link>
                          {p.usage && <span className="muted"> · {p.usage}</span>}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="num">{s.monthlyUsd === null ? "—" : `$${s.monthlyUsd.toFixed(2)}`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      <p className="stamp">Admins add services here as the lab grows: an OpenSearch domain, SES for email, a queue. Projects link the ones they use from their Infra tab.</p>
    </div>
  );
}
